import React, {ChangeEvent, useEffect, useState, useRef} from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

import {Tooltip} from '@components/modal/Tooltip';
import NumericInput from '@components/inputs/NumericInput';
import DropDownMenu, {IChoice} from '@components/inputs/DropDownMenu';
import {UserFormStates, useUserJourneyContext} from '@context/UserJourneyContext';
import {HorizontalLineSep} from "@components/separators/horizontal_line";
import Button, { ButtonStyle } from '@components/buttons/Button/Button';
import { Country } from '@utils/constants/localisation';
import { SubscriptionService } from '@services/index';
import EllipticalSpinner from '@components/spinners/Elliptical/Elliptical';
import Warning from '@components/modal/info/Warning';


const ivoryClr: string = '#FFFFF0';
const paleLimeGreenClr: string = '#ddffdd';
const paleRedClr: string = '#ffdddd';


export function DynamicPricingAU() {
    const {formState, updateFormState} = useUserJourneyContext();
    const {splendInitiatives, contractDetails, selectedPotential} = formState;
    // The dynamic pricing form contains 4 dropdowns which are used to calculate a total discount
    // These values are in major currency units ($ or £)
    // In AU the initial value of these should come from the SelectedPotential in the context.
    // TODO do we overwrite the selected potential values?
    // The defaults correspond to the default values in the dropdowns - so the discount is correct on first render
    const [tripDiscount, setTripDiscount] = useState<number>(0);
    const [kmAllowanceDiscount, setKmAllowanceDiscount] = useState<number>(20);
    const [minTermDiscount, setMinTermDiscount] = useState<number>(0);
    const [vehicleUtilDiscount, setVehicleUtilDiscount] = useState<number>(20);
    // Total discount is recalculated every time one of the 4 discounts changes
    const [totalDiscount, setTotalDiscount] = useState<number>(40);


    useEffect(() => {
        // Calculate the total dynamic pricing discount
        // A sum of the 4 individual discounts
        const totalDiscount = tripDiscount + kmAllowanceDiscount + minTermDiscount + vehicleUtilDiscount;
        const adjustedWeeklyFee = Number((contractDetails.planWeeklyFee - totalDiscount).toFixed(2));
        //Update state with discount
        updateFormState(UserFormStates.splendInitiatives, {
            totalDynamicPricing: totalDiscount,
            adjustedWeeklyHireFee: adjustedWeeklyFee
            //TODO is this used in flexi template?
        });
        setTotalDiscount(totalDiscount);
        //TODO Do we need to set the other values in splendInitiatives?
    }, [tripDiscount, kmAllowanceDiscount, minTermDiscount, vehicleUtilDiscount]);


    //Trip count (completed trips)
    const tripCountOptions: IChoice[] = [
        // first item in list is also the default plan
        {value: '0 - 2999', label: '0 - 2999', enabled: true},
        {value: '3000 - 6999', label: '3000 - 6999', enabled: true},
        {value: '7000+', label: '7000+', enabled: true},
    ];
    const calculateTripDiscount = (tripCount: string): number => {
        const lowTripCountDiscount = 0 // no discount applied
        const moderateTripCountDiscount = 20 // Discount of $20 applied
        const highTripCountDiscount = 50 // Discount of $50 applied

        const lowTripCount = "0 - 2999"
        const moderateTripCount = "3000 - 6999"
        const highTripCount = "7000+"

        if (tripCount === lowTripCount) return lowTripCountDiscount;
        if (tripCount === moderateTripCount) return moderateTripCountDiscount;
        if (tripCount === highTripCount) return highTripCountDiscount;
        return 0;
    };

    const handleTripCountSelect = (e: ChangeEvent<HTMLSelectElement>) => {
        const value = e.currentTarget.value;
        setTripDiscount(calculateTripDiscount(value));
    };

    // KM allowance
    const kmAllowanceOptions: IChoice[] = [
        // first item in list is also the default plan
        {value: '1000', label: '1000', enabled: true},
        {value: '1200', label: '1200', enabled: true},
    ];
    const calculateKmAllowanceDiscount = (kmAllowance: string): number => {
        const allowance = Number(kmAllowance);
        return allowance === 1200 ? 0 : 20;
    };

    const handleKmAllowanceSelect = (e: ChangeEvent<HTMLSelectElement>) => {
        const value = e.currentTarget.value;
        setKmAllowanceDiscount(calculateKmAllowanceDiscount(value));
    };

    // Minimum term
    const minimumPeriodWeeksOptions: IChoice[] = [
        // first item in list is also the default plan
        {value: '6', label: '6', enabled: true},
        {value: '10', label: '10', enabled: true},
        {value: '14', label: '14', enabled: true},
    ];
    const calculateMinimumTermDiscount = (minimumTerm: string): number => {
        enum MinimumPeriodWeeks {
            sixWeeks = 6,
            tenWeeks = 10,
            fourteenWeeks = 14,
        }

        const minTerm = Number(minimumTerm);
        const noDiscount = 0 // No discount applied
        const moderateDiscount = 10 //Discount of $10 applied
        const highDiscount = 20 // Discount of $20 applied

        if (minTerm === MinimumPeriodWeeks.sixWeeks) return noDiscount;
        if (minTerm === MinimumPeriodWeeks.tenWeeks) return moderateDiscount;
        if (minTerm === MinimumPeriodWeeks.fourteenWeeks) return highDiscount;
        return 0
    };
    const handleMinimumTermSelect = (e: ChangeEvent<HTMLSelectElement>) => {
        const value = e.currentTarget.value;
        setMinTermDiscount(calculateMinimumTermDiscount(value));
    };

    // Vehicle utilisation
    const vehicleUtilisationOptions: IChoice[] = [
        // first item in list is also the default plan
        {value: 'Yes', label: 'Yes', enabled: true},
        {value: 'No', label: 'No', enabled: true},

    ];
    const calculateVehicleUtilisationDiscount = (vehicleUtilisation: string): number => {
        const noDiscount = 0 // No discount applied
        const discount = 20 // Discount of $20 applied
        return vehicleUtilisation == "Yes" ? discount : noDiscount;
    };

    const handleVehicleUtilisationSelect = (e: ChangeEvent<HTMLSelectElement>) => {
        const value = e.currentTarget.value;
        setVehicleUtilDiscount(calculateVehicleUtilisationDiscount(value));
    };


    // Determine default values for the dropdowns - these come from the SelectedPotential context
    // If the value in the context is not in the list of options, then the first item in the list is used.
    const defaultVehicleUtilisationOption =
        minimumPeriodWeeksOptions.find(option => option.value === selectedPotential.vehicle_utilization)
        || vehicleUtilisationOptions[0]
    const defaultMinimumPeriodWeeksOption =
        minimumPeriodWeeksOptions.find(option => option.value === selectedPotential.min_period_weeks)
        || minimumPeriodWeeksOptions[0]
    const defaultKMAllowanceOption =
        kmAllowanceOptions.find(option => option.value === selectedPotential.distance_allowance)
        || kmAllowanceOptions[0]
    const defaultTripCountOption =
        tripCountOptions.find(option => option.value === selectedPotential.trip_count)
        || tripCountOptions[0]
    // Update states to match the default options
    useEffect(() => {
        setTripDiscount(calculateTripDiscount(defaultTripCountOption.value));
        setKmAllowanceDiscount(calculateKmAllowanceDiscount(defaultKMAllowanceOption.value));
        setMinTermDiscount(calculateMinimumTermDiscount(defaultMinimumPeriodWeeksOption.value));
        setVehicleUtilDiscount(calculateVehicleUtilisationDiscount(defaultVehicleUtilisationOption.value));
    }, []);

    const rowStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
    };

    const itemStyle: React.CSSProperties = {
        flex: 1,
        padding: '10px',
        boxSizing: 'border-box',
    };

    const textStyle: React.CSSProperties = {
        ...itemStyle,
        fontWeight: 'bold',
    };

    const numberStyle: React.CSSProperties = {
        ...itemStyle,
        textAlign: 'right',
    };

    const lineStyle: React.CSSProperties = {
        color: '#E1E1E1',
        borderColor: '#E1E1E1',
    }

    return (
        <div style={{margin: '2.5vh 0'}}>
            <div>
                <div style={textStyle}>Trip count</div>
                <DropDownMenu
                    menuName='tripCountDropdownMenu'
                    defaultVal={defaultTripCountOption}
                    required={true}
                    choices={tripCountOptions}
                    onSelect={handleTripCountSelect}
                    testId='tripCountDropdownMenu'
                />
            </div>

            <div style={rowStyle}>
                <div style={textStyle}>Discount ($)</div>
                <div style={numberStyle}>{tripDiscount.toString()}</div>
            </div>

            <div>
                <hr style={lineStyle}/>
            </div>

            <div>
                <div style={textStyle}>KM allowance</div>
                <DropDownMenu
                    menuName='kmAllowanceDropdownMenu'
                    defaultVal={defaultKMAllowanceOption}
                    required={true}
                    choices={kmAllowanceOptions}
                    onSelect={handleKmAllowanceSelect}
                    testId='kmAllowanceDropdownMenu'

                />
            </div>

            <div style={rowStyle}>
                <div style={textStyle}>Discount ($)</div>
                <div style={numberStyle}>{kmAllowanceDiscount.toString()}</div>
            </div>

            <div>
                <hr style={lineStyle}/>
            </div>

            <div>
                <div style={textStyle}>Minimum term (weeks)</div>
                <DropDownMenu
                    menuName='minimumTermDropdownMenu'
                    defaultVal={defaultMinimumPeriodWeeksOption}
                    required={true}
                    choices={minimumPeriodWeeksOptions}
                    onSelect={handleMinimumTermSelect}
                    testId='minimumTermDropdownMenu'
                />
            </div>

            <div style={rowStyle}>
                <div style={textStyle}>Discount ($)</div>
                <div style={numberStyle}>{minTermDiscount.toString()}</div>
            </div>

            <div>
                <hr style={lineStyle}/>
            </div>

            <div>
                <div style={textStyle}>Vehicle utilisation (Greater than 6)</div>
                <DropDownMenu
                    menuName='vehicleUtilisationDropdownMenu'
                    defaultVal={defaultVehicleUtilisationOption}
                    required={true}
                    choices={vehicleUtilisationOptions}
                    onSelect={handleVehicleUtilisationSelect}
                    testId='vehicleUtilisationDropdownMenu'
                />
            </div>

            <div style={rowStyle}>
                <div style={textStyle}>Discount ($)</div>
                <div style={numberStyle}>{vehicleUtilDiscount.toString()}</div>
            </div>

            <div>
                <hr style={lineStyle}/>
            </div>

            <div style={rowStyle}>
                <div style={textStyle}>Total weekly discount ($)</div>
                <div style={numberStyle}>{totalDiscount.toString()}</div>
            </div>

            <div style={rowStyle}>
                <div style={textStyle}>Adjusted weekly hire fee ($)</div>
                <div style={numberStyle}>{splendInitiatives.adjustedWeeklyHireFee.toFixed(2)}</div>
                {/*//TODO use a state for this value?*/}
            </div>
        </div>
    );
};


export const DynamicPricingUK = () => {
    // UK form does not use the information from selectedPotential or splendInitiatives to inform the default values of
    // these dropdowns

    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const {formState: {splendInitiatives, contractDetails}, updateFormState} = useUserJourneyContext();

    // The state below stores the user's input for the discount. It's attempted to be initialised with the value from
    // context for when the user returns to this view and having a priorly set value.
    const [dynamicPricingDiscount, setDynamicPricingDiscount] = useState<number>(splendInitiatives.totalDynamicPricing || 0);

    // Generic search function that is expected to always find at most one item within the
    // given array of options. For the intended usages from this component, Undefined is
    // also fine. One example is when this is used to get an option using a context value
    // to find it, the value is not yet populated, so the returned value is Undefined.
    const findOptionFromList = (optionValue: string | number, choices: IChoice[]): IChoice => choices.filter(option => option.value === String(optionValue))[0];
    const findOptionFromListByLabel = (optionLabel: string, choices: IChoice[]): IChoice => choices.filter(option => option.label === String(optionLabel))[0];

    // The value represents the discount to be applied depending on the choice and the
    // label is what's being displayed to the user.
    const tripCountOptions: IChoice[] = [
        {value: '10', label: '301 - 1000', enabled: true},
        {value: '20', label: '1001 - 2000', enabled: true},
        {value: '30', label: '2001 - 3000', enabled: true},
        {value: '40', label: '3001+', enabled: true},
    ];
    // Custom tailored function to search by value only inside the tripCountOptions list
    const findOptionFromTripsByValue = (optionValue: string): IChoice => findOptionFromList(optionValue, tripCountOptions);
    const findOptionFromTripsByLabel = (optionLabel: string): IChoice => findOptionFromListByLabel(optionLabel, tripCountOptions);
    const [tripCount, setTripCount] = useState<IChoice>(findOptionFromTripsByLabel(splendInitiatives.tripCount) || tripCountOptions[0]);

    // The value represents the discount to be applied depending on the choice and the
    // label is what's being displayed to the user.
    const distanceAllowanceOptions: IChoice[] = [
        {value: '30', label: '700', enabled: true},
        {value: '0', label: '1000', enabled: true},
    ];
    // Custom tailored function to search by value only inside the distanceAllowanceOptions list
    const findOptionFromDistanceAllowancesByValue = (optionValue: string) => findOptionFromList(optionValue, distanceAllowanceOptions);
    const findOptionFromDistanceAllowances = (optionLabel: string) => findOptionFromListByLabel(optionLabel, distanceAllowanceOptions);
    const [distanceAllowance, setDistanceAllowance] = useState<IChoice>(findOptionFromDistanceAllowances(splendInitiatives.weeklyDistanceAllowance) || distanceAllowanceOptions[0]);

    // The value does not have any special meaning. It is used within a convention where
    // 0 is False and 1 is True (similarly to Yes and No). In the long run, value field
    // should accept other types than string.
    const driverRatingOptions: IChoice[] = [
        {value: '0', label: 'No', enabled: true},
        {value: '1', label: 'Yes', enabled: true},
    ];
    // Custom tailored function to search by value only inside the driverRatingOptions list
    const findOptionFromDriverRatings = (optionValue: string) => findOptionFromList(optionValue, driverRatingOptions);
    const findOptionFromDriverRatingsByLabel = (optionLabel: string) => findOptionFromListByLabel(optionLabel, driverRatingOptions);
    const [driverRating, setDriverRating] = useState<IChoice>(findOptionFromDriverRatingsByLabel(splendInitiatives.driverRating) || driverRatingOptions[0]);

    // The state below is initialised with null for cases when the dynamicPricingDiscount state field was not set,
    // meaning that the discount is not valid, nor invalid. Otherwise, default it with the bool of dynamicPricingDiscount.
    const [isDiscountValueValid, setIsDiscountValueValid] = useState<null | boolean>(!!dynamicPricingDiscount || null);
    const [discountCkInProgress, setDiscountCkInProgress] = useState<boolean>(false);
    const [discountCheckSvError, setDiscountCheckSvError] = useState<boolean>(false);

    // This ref is necessary to have a pointer to the discount input from code that's not acting or is linked
    // with the input's onChange function.
    const discountInputRef = useRef<HTMLInputElement>(null);
    
    useEffect(
        () => {  
            // On first render, set the validity message, if there's no prior valid value, to kick in if
            // user attempts to progress with the journey without validating the discount first. It is assumed
            // that, given the conditions of setting the context-state pair for the discount, the context can't
            // store an invalid value, meaning that on a first render when having a value in the context, it
            // must be valid, hence no need to set the validity msg.
            if (discountInputRef.current && !isDiscountValueValid)
                discountInputRef.current.setCustomValidity('Please validate the coupon value');
        },
        []
    );

    // The line below is a hotfix because for UK journey the context field is populated as Undefined
    // from the customer details hook. The field is used in the payload to the backend and is not allowed to
    // be missing from it (as it would happen for an Undefined value)000000000000000.
    useEffect(() => {
        updateFormState(UserFormStates.splendInitiatives, {vehicleUtilisation: '0'})
    }, []);

    useEffect(() => {
        if (!isDiscountValueValid) return;

        // Update the context each time one of trips, distance or driver rating options change.
        // The discount values are converted from string to number because they come from the
        // IChoice interface, which requires the value field to always be a string. The context
        // discount field shall be a number, hence the data conversion necessity.
        const newDiscount = (dynamicPricingDiscount || 0);
        const adjustedWeeklyHireFee = Number((contractDetails.planWeeklyFee - newDiscount).toFixed(2));

        updateFormState(UserFormStates.splendInitiatives, {
            totalDynamicPricing: newDiscount,
            adjustedWeeklyHireFee: adjustedWeeklyHireFee
            //TODO is this used in flexi template?
        });
    }, [dynamicPricingDiscount, isDiscountValueValid]);

    useEffect(() => updateFormState(UserFormStates.splendInitiatives, {tripCount: tripCount.label}), [tripCount]);
    useEffect(() => updateFormState(UserFormStates.splendInitiatives, {weeklyDistanceAllowance: distanceAllowance.label}), [distanceAllowance]);
    useEffect(() => updateFormState(UserFormStates.splendInitiatives, {driverRating: driverRating.label}), [driverRating]);

    const onDiscountChange = (newValue: number) => {
        setDynamicPricingDiscount(newValue);
        setIsDiscountValueValid(null);
        if (discountInputRef.current) discountInputRef.current.setCustomValidity('Please validate the coupon value');
    };

    const onValidateDiscount = async () => {
        if (discountInputRef.current){
            setDiscountCkInProgress(true);

            const currentDiscountElement = discountInputRef.current;
            SubscriptionService.getCoupons(
                { country: Country.GB, 'value[is]': dynamicPricingDiscount, 'type[is]': 'dynamic' },
                { msalContext, isAuthenticated }
            ).then((coupons) => {
                setDiscountCheckSvError(false);                
                if ((coupons.length == 0)){
                    currentDiscountElement.setCustomValidity('No coupon found for this discount value');
                    currentDiscountElement.reportValidity();
                    setIsDiscountValueValid(false);
                } else {
                    currentDiscountElement.setCustomValidity('');
                    setIsDiscountValueValid(true);
                }
            }).catch(() => {
                // No error message to attach to the discount input. However, since the discount couldn't be
                // validated, it should be set as invalid. Set state to display error.
                currentDiscountElement.setCustomValidity('');
                setIsDiscountValueValid(null);
                setDiscountCheckSvError(true);
            }).finally(() => {
                setDiscountCkInProgress(false);
            });
        }
    };

    const closeModal = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setDiscountCheckSvError(false);
    };

    const getValidationBgColor = (state: null | boolean) => {
        switch (state){
            case null: return ivoryClr;
            case true: return paleLimeGreenClr;
            case false: return paleRedClr;
        }
    };

    const itemStyle: React.CSSProperties = {
        flex: 1,
        padding: '10px',
        boxSizing: 'border-box',
    };

    const textStyle: React.CSSProperties = {...itemStyle, fontWeight: 'bold'};

    const rowStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
    };

    const numberStyle: React.CSSProperties = {...itemStyle, textAlign: 'right'};

    return (<>
        {discountCheckSvError && <WarningModal onClickClose={closeModal} />}
        <div style={{margin: '2.5vh 0'}}>
            <div>
                <div style={textStyle}>
                    Is driver rating greater or equal than 4.8?
                    <Tooltip
                        message='The discount for the number of trips is counted only if driver rating is at least 4.8'
                        imgIconStyles={{verticalAlign: 'middle', marginLeft: '5px'}}/>
                </div>
                <DropDownMenu
                    menuName='driverRating'
                    defaultVal={driverRating}
                    required={true}
                    choices={driverRatingOptions}
                    onSelect={(e: ChangeEvent<HTMLSelectElement>) => setDriverRating(findOptionFromDriverRatings(e.target.value))}
                    testId='driverRatingDropdownMenu'
                />
                <div style={textStyle}>Trip count</div>
                <DropDownMenu
                    menuName='tripCountDropdownMenu'
                    defaultVal={tripCount}
                    required={true}
                    choices={tripCountOptions}
                    onSelect={(e: ChangeEvent<HTMLSelectElement>) => setTripCount(findOptionFromTripsByValue(e.target.value))}
                    testId='tripCountDropdownMenu'
                />
                <div style={textStyle}>Mileage allowance</div>
                <DropDownMenu
                    menuName='distanceAllowanceDropdownMenu'
                    defaultVal={distanceAllowance}
                    required={true}
                    choices={distanceAllowanceOptions}
                    onSelect={(e: ChangeEvent<HTMLSelectElement>) => setDistanceAllowance(findOptionFromDistanceAllowancesByValue(e.target.value))}
                    testId='distanceAllowanceDropdownMenu'
                />
            </div>
            <div>
                <div style={textStyle}>Total weekly discount (£)</div>
                <div style={{display: 'flex', flexDirection: 'row'}}>
                    <div style={{'minWidth': '50%', position: 'relative', display: 'inline-block'}}>
                        <NumericInput
                            name='DynamicPricingDiscountInput'
                            dataTestId='dynamic-pricing-discount-input'
                            style={{'minWidth': '100%', backgroundColor: getValidationBgColor(isDiscountValueValid)}}
                            defaultVal={dynamicPricingDiscount}
                            required={true}
                            onChange={(e) => onDiscountChange(Number(e.currentTarget.value))}
                            ref={discountInputRef}
                        />
                        {discountCkInProgress && 
                        <div style={{position: 'absolute', transform: 'translateY(-40%)', top: '50%', right: '0.5vw'}}>
                            <EllipticalSpinner/>
                        </div>}
                    </div>
                    <div style={{marginLeft: '2.5vw'}}>
                        <Button 
                            label={'Validate coupon value'}
                            btnType={ButtonStyle.secondary}
                            onClickFunc={onValidateDiscount} 
                            testId='discount-validate-btn'/>
                    </div>
                </div>
            </div>
            <HorizontalLineSep/>
            <div style={rowStyle}>
                <div style={textStyle}>Adjusted weekly hire fee (£)</div>
                <div style={numberStyle}>{splendInitiatives.adjustedWeeklyHireFee.toFixed(2)}</div>
            </div>
        </div>
    </>
    );
};


const WarningModal = ({ onClickClose }: { onClickClose: React.MouseEventHandler }) => (
    <Warning
        message="Something went wrong"
        additionalInfo='We’re sorry, we were unable to process your request. Try refreshing the page or contact IT Support for assistance'
        onClickClose={onClickClose}
    />
);
